import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api/axiosConfig';
import AuthLayout from '../../../layouts/AuthLayout';
import AuthSeparatorLink from '../components/AuthSeparatorLink';
import SubmitButton from '../components/SubmitButton';
import { TextInput } from 'flowbite-react';
import Label from '../../../components/Label';
import errorHandler from '../../../utils/errorHandler';
import ShowValidationMessage from '../../../components/ShowValidationMessage';
import PasswordInput from '../../../components/PasswordInput';
import InputMask from 'react-input-mask';
import useRedirectIfAuthenticated from '../../../hooks/useRedirectIfAuthenticated';
import FullScreenLoader from '../../../components/FullScreenLoader';
import API_ENDPOINTS from '../../../services/api/endpoints';
import { toast } from 'react-toastify';

const SignupPage = () => {

    // if user is already logged in redirect back to the dashboard
    const isLoadingAuthCheck = useRedirectIfAuthenticated(); 

    const [formData, setFormData] = useState({
        name: '',
        firstName:'',
        lastName:'',
        email: '',
        businessPhone: '',
        contactPhone: '',
        password: '',
        passwordConfirmation: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'CPA - Signup';;
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setErrors(prev => ({
            ...prev,
            [name]: '',
            general: '' // Clear general error when user types
        }));
    };

    const validateForm = () => {
        let newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Business name is required.';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email address is required.';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email address is invalid.';
        }
        if (!formData.businessPhone.trim()) {
            newErrors.businessPhone = 'Business phone is required.';
        }

        if (!formData.contactPhone.trim()) {
            newErrors.contactPhone = 'Contact phone is required.';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required.';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters.';
        }
        if (!formData.passwordConfirmation) {
            newErrors.passwordConfirmation = 'Password confirmation is required.';
        } else if (formData.password !== formData.passwordConfirmation) {
            newErrors.passwordConfirmation = 'Passwords do not match.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        if (validateForm()) {
            try {
                const payload = {
                    ...formData,
                    // Strip all non-numeric characters from the phone number
                    businessPhone: formData.businessPhone.replace(/\D/g, ''),
                    contactPhone: formData.contactPhone.replace(/\D/g, ''),

                };
                const response = await api.post(API_ENDPOINTS.AUTH.SIGNUP, payload);

                // alert(response.data.statuscode);

                if(response.data.statuscode==212) {
                    errorHandler(response.data, setErrors);

                }else{
                    console.log('Registration successful:', response.data);

                    toast.success("Registration successful please login."); 


                    // toast.success("Succes please check your Email for Email verification."); // Show success toast
                    navigate('/'); 

                }

              

            } catch (error) {
                console.error('Registration failed:', error);
                errorHandler(error, setErrors);
            } finally {
                setLoading(false);
            }
        } else {
            setLoading(false);
            console.log('Client-side validation failed.');
        }
    };

     if (isLoadingAuthCheck) { // If the background auth check is still running
        return <FullScreenLoader />
    }

    return (
        <AuthLayout title="Create account">


            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                {errors.general && (
                    <div className="p-3 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
                        {errors.general}
                    </div>
                )}

                <div className='sm:col-span-2'>


                    <Label htmlFor="Name" label="Business Name" />
                    <TextInput
                        id="Name"
                        name="name"
                        type="text"
                        autoComplete="organization"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className={`input-field ${errors.name ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="Your company name"
                        aria-invalid={errors.name ? "true" : "false"}
                        aria-describedby="business-name-error"
                        disabled={loading}
                    />
                    <ShowValidationMessage error={errors.name} />


                </div>

                <div className='sm:col-span-2'>
                    <Label htmlFor="First name" label="First name" />
                    <TextInput
                        id="firstName"
                        name="firstName"
                        type="text"
                        autoComplete="organization"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        className={`input-field ${errors.firstName ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="Your firstname"
                        aria-invalid={errors.firstName ? "true" : "false"}
                        aria-describedby="firstName-name-error"
                        disabled={loading}
                    />
                    <ShowValidationMessage error={errors.firstName} />
                </div>


                   <div className='sm:col-span-2'>
                    <Label htmlFor="Last name" label="Last name" />
                    <TextInput
                        id="lastName"
                        name="lastName"
                        type="text"
                        autoComplete="organization"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        className={`input-field ${errors.lastName ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="Your lastname"
                        aria-invalid={errors.lastName ? "true" : "false"}
                        aria-describedby="lastName-name-error"
                        disabled={loading}
                    />
                    <ShowValidationMessage error={errors.lastName} />
                </div>


                <div className='sm:col-span-2'>


                    <Label htmlFor="Email" label="Email" />

                    <TextInput
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className={`input-field ${errors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="you@example.com"
                        aria-invalid={errors.email ? "true" : "false"}
                        aria-describedby="email-error"
                        disabled={loading}
                    />
                    <ShowValidationMessage error={errors.email} />

                </div>

                <div className='sm:col-span-2'>
                    <Label htmlFor="phone" label="Business Phone" />
                    <InputMask
                        mask="(999) 999-9999" // Defines the mask (9 is for any digit 0-9)
                        maskChar="_" // Placeholder character for empty digits
                        value={formData.businessPhone}
                        onChange={handleChange}
                    >
                        {(inputProps) => (
                            <TextInput
                                {...inputProps} // Pass all InputMask's props to TextInput
                                id="businessPhone"
                                name="businessPhone"
                                type="tel" // Use type="tel" for phone numbers
                                placeholder="(123) 456-7890"
                                color={errors.businessPhone ? 'failure' : 'gray'}
                                required={false} // Phone is optional in your current validation
                            />
                        )}
                    </InputMask>

                    <ShowValidationMessage error={errors.businessPhone} />

                </div>


                 <div className='sm:col-span-2'>
                    <Label htmlFor="phone" label="Contact Phone" />
                    <InputMask
                        mask="(999) 999-9999" // Defines the mask (9 is for any digit 0-9)
                        maskChar="_" // Placeholder character for empty digits
                        value={formData.contactPhone}
                        onChange={handleChange}
                    >
                        {(inputProps) => (
                            <TextInput
                                {...inputProps} // Pass all InputMask's props to TextInput
                                id="contactPhone"
                                name="contactPhone"
                                type="tel" // Use type="tel" for phone numbers
                                placeholder="(123) 456-7890"
                                color={errors.contactPhone ? 'failure' : 'gray'}
                                required={false} // Phone is optional in your current validation
                            />
                        )}
                    </InputMask>

                    <ShowValidationMessage error={errors.contactPhone} />

                </div>

                <div className="sm:col-span-2">

                    <Label htmlFor="password" label="Password" />
                    <PasswordInput
                        id="password"
                        name="password"
                        placeholder="••••••••"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        disabled={loading}
                    />

                    <ShowValidationMessage error={errors.password} />



                </div>

                <div className="sm:col-span-2">
                    <Label htmlFor="passwordConfirmation" label="Confirm Password" />
                    <PasswordInput
                        id="passwordConfirmation"
                        name="passwordConfirmation"
                        placeholder="••••••••"
                        required
                        value={formData.passwordConfirmation}
                        onChange={handleChange}
                        disabled={loading}
                        error={errors.passwordConfirmation}
                    />

                    <ShowValidationMessage error={errors.passwordConfirmation} />

                </div>

                <div className="pt-2">
                    <SubmitButton isLoading={loading} loadingText="Signing up...">
                        Signup
                    </SubmitButton>
                </div>
            </form>

            {/* --- Start of "Or" Separator and Link --- */}
            <AuthSeparatorLink
                textDescription="Already registered? "
                linkText="Login"
                toPath="/"
            />
        </AuthLayout>

    );
};

export default SignupPage;